import React from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logout as LogoutIcon } from '@mui/icons-material';
import DashboardIcon from '../icons/dashboard.svg';
import UsersIcon from '../icons/users.svg';
import CardIcon from '../icons/card.svg';
import FeaturesIcon from '../icons/features.svg';
import AiIcon from '../icons/ai.svg';
import CodeIcon from '../icons/code.svg';
import CustomerServiceIcon from '../icons/customer-service.svg';
import InfrastructureIcon from '../icons/infrrastructure.svg';
import LegalIcon from '../icons/legal.svg';
import ReportIcon from '../icons/report.svg';
import CalendarIcon from '../icons/calendar.svg';
import LabsIcon from '../icons/labs.svg';
import FolderIcon from '../icons/folder.svg';
import AdminIcon from '../icons/admin.svg';
import AletheiaLogo from '../icons/aletheia_logo.svg';
import { useAuth } from '../contexts/AuthContext';

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 80,
  height: '100vh',
  backgroundColor: '#1E1E1E',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  position: 'fixed',
  left: 0,
  top: 0,
  zIndex: 1000,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StyledIconButton = styled(IconButton)<{ isactive?: string }>(({ theme, isactive }) => ({
  width: 48,
  height: 48,
  borderRadius: 8,
  backgroundColor: isactive === "true" ? '#6B46C1' : 'transparent',
  color: isactive === "true" ? '#FFFFFF' : '#A0AEC0',
  '&:hover': {
    backgroundColor: isactive === "true" ? '#6B46C1' : '#4A5568',
  },
  '& img': {
    width: 24,
    height: 24,
    filter: isactive === "true" ? 'brightness(0) saturate(100%) invert(8%) sepia(8%) saturate(7482%) hue-rotate(250deg) brightness(94%) contrast(95%)' : 'brightness(0) invert(1)',
  },
}));

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const theme = useTheme();

  const menuItems = [
    { icon: DashboardIcon, label: 'Dashboard', path: '/home', active: location.pathname === '/home' },
    { icon: UsersIcon, label: 'Users', path: '/users', active: location.pathname === '/users' || location.pathname === '/companies' || location.pathname === '/admin-users' },
    { icon: CardIcon, label: 'Billing', path: '/billing', active: location.pathname === '/billing' },
    { icon: FeaturesIcon, label: 'Features', path: '/features', active: location.pathname === '/features' },
    { icon: AiIcon, label: 'AI', path: '/ai', active: location.pathname === '/ai' },
    { icon: CodeIcon, label: 'API Monitoring', path: '/api-monitoring', active: location.pathname.startsWith('/api-monitoring') },
    { icon: CustomerServiceIcon, label: 'Customer Support', path: '/support', active: location.pathname === '/support' },
    { icon: InfrastructureIcon, label: 'Infrastructure', path: '/infrastructure', active: location.pathname === '/infrastructure' },
    { icon: LegalIcon, label: 'Compliance & Legal', path: '/legal', active: location.pathname === '/legal' },
    { icon: ReportIcon, label: 'Reports', path: '/reports', active: location.pathname === '/reports' },
    { icon: CalendarIcon, label: 'Calendar', path: '/calendar', active: location.pathname === '/calendar' },
    { icon: LabsIcon, label: 'Labs', path: '/labs', active: location.pathname === '/labs' },
    { icon: FolderIcon, label: 'Document Intelligence', path: '/documents', active: location.pathname === '/documents' },
    { icon: AdminIcon, label: 'Websites', path: '/analytics', active: location.pathname === '/analytics' || location.pathname === '/blog' },
    { icon: AdminIcon, label: 'Admin', path: '/admin', active: location.pathname === '/admin' },
  ];

  return (
    <SidebarContainer>
      <LogoContainer>
        <img 
          src={AletheiaLogo} 
          alt="Aletheia" 
          style={{ 
            width: 56, 
            height: 56,
            filter: 'brightness(0) invert(1)',
            opacity: 0.9
          }} 
        />
      </LogoContainer>
      
      <IconContainer>
        {menuItems.map((item, index) => (
          <Tooltip key={index} title={item.label} placement="right">
            <StyledIconButton 
              isactive={item.active ? "true" : "false"}
              onClick={() => navigate(item.path)}
            >
              <img src={item.icon} alt={item.label} />
            </StyledIconButton>
          </Tooltip>
        ))}
      </IconContainer>
      
      {/* Logout button at the bottom */}
      <Box sx={{ marginTop: 'auto', marginBottom: theme.spacing(2) }}>
        <Tooltip title="Logout" placement="right">
          <StyledIconButton 
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            <LogoutIcon sx={{ color: '#A0AEC0', fontSize: 24 }} />
          </StyledIconButton>
        </Tooltip>
      </Box>
    </SidebarContainer>
  );
};

export default Sidebar; 